import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Check,
  Cpu,
  Download,
  HardDrive,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import { api } from '@/services/api'
import { answersInChat, CATEGORY_LABELS, groupByCategory } from '@/services/modelCategory'
import { DeviceCapabilityCard } from '@/components/DeviceCapabilityCard'
import { ModelFitBadge } from '@/components/ModelFitBadge'
import type {
  LocalModel,
  ModelRuntimeStatus,
  ModelUpdateStatus,
} from '@/services/types'

/** Pull progress keyed by model id, driven by polling while a pull is active. */
type PullState = Record<
  string,
  { percent: number; phase: string; completed: number; total: number }
>

/** Best fit first; mirrors the server's tier ordering. */
const FIT_ORDER: LocalModel['fitTier'][] = [
  'RECOMMENDED',
  'WORKS',
  'TIGHT',
  'UNKNOWN',
  'TOO_LARGE',
  'UNSUPPORTED',
]

/**
 * Where a tier sorts, including one this build has never heard of.
 *
 * `indexOf` answers -1 for an unknown tier, which sorts it *above*
 * RECOMMENDED -- so a verdict this client cannot interpret would lead the
 * page. The server owns the tiers and has added one before; an unrecognised
 * verdict belongs at the bottom, where being wrong about it costs nothing.
 */
function fitRank(tier: LocalModel['fitTier']): number {
  const at = FIT_ORDER.indexOf(tier)
  return at === -1 ? FIT_ORDER.length : at
}

export function ModelsPage() {
  const [models, setModels] = useState<LocalModel[]>([])
  const [status, setStatus] = useState<ModelRuntimeStatus | null>(null)
  const [query, setQuery] = useState('')
  const [pulls, setPulls] = useState<PullState>({})
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  /** Update state keyed by model id, from the cached server-side check. */
  const [updates, setUpdates] = useState<Record<string, ModelUpdateStatus>>({})

  const reload = useCallback(async () => {
    try {
      const [list, runtime] = await Promise.all([
        api.listLocalModels(),
        api.modelRuntimeStatus(),
      ])
      setModels(list)
      setStatus(runtime)
      setError(null)

      // Separate and non-blocking: this reaches other people's servers, and a
      // publisher being slow or down must not stop the page listing models.
      void api
        .listModelUpdates()
        .then((result) =>
          setUpdates(Object.fromEntries(result.models.map((m) => [m.modelId, m]))),
        )
        .catch(() => setUpdates({}))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load models')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  // While anything is downloading, poll real progress and re-check installed
  // state so a finished pull flips to "Installed" without a manual reload.
  useEffect(() => {
    if (Object.keys(pulls).length === 0) return

    const tick = async () => {
      try {
        const active = await api.listPulls()
        setPulls((current) => {
          const next: PullState = {}
          for (const id of Object.keys(current)) {
            const live = active.find((p) => p.modelId === id)
            if (!live) {
              // Not yet reported, or already evicted: hold the last state
              // rather than dropping the row mid-download.
              next[id] = current[id]
              continue
            }
            if (live.error) {
              setError(live.error)
              continue
            }
            if (live.finished) continue
            next[id] = {
              percent: live.percent,
              phase: live.phase,
              completed: live.completed,
              total: live.total,
            }
          }
          return next
        })
        if (active.some((p) => p.finished)) void reload()
      } catch {
        // Polling is best effort; a blip must not clear a running download.
      }
    }

    const timer = setInterval(() => void tick(), 1500)
    return () => clearInterval(timer)
  }, [pulls, reload])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matches = !needle
      ? models
      : models.filter(
          (m) =>
            m.id.toLowerCase().includes(needle) ||
            m.name.toLowerCase().includes(needle) ||
            m.family.toLowerCase().includes(needle) ||
            m.capabilities.some((c) => c.includes(needle)),
        )

    // Installed models first, then whatever suits this machine best. A user
    // scanning the list should not have to read every row to find the one
    // worth downloading.
    return [...matches].sort((a, b) => {
      if (a.installed !== b.installed) return a.installed ? -1 : 1
      const byFit = fitRank(a.fitTier) - fitRank(b.fitTier)
      if (byFit !== 0) return byFit
      return b.sizeBytes - a.sizeBytes
    })
  }, [models, query])

  // Grouped after sorting, so each category keeps the installed-first,
  // best-fit-next order rather than being re-shuffled by the grouping.
  const groups = useMemo(() => groupByCategory(filtered), [filtered])

  const applyUpdate = async (model: LocalModel) => {
    // The server replaces the file, so this is a fresh download: show it in
    // the same progress UI a first install uses rather than leaving the row
    // looking idle for several minutes.
    setPulls((p) => ({
      ...p,
      [model.id]: { percent: 0, phase: 'starting', completed: 0, total: model.sizeBytes },
    }))
    setUpdates((u) => {
      const next = { ...u }
      delete next[model.id]
      return next
    })
    try {
      await api.updateModel(model.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not update ${model.name}`)
      setPulls((p) => {
        const next = { ...p }
        delete next[model.id]
        return next
      })
      void reload()
    }
  }

  const pull = async (model: LocalModel) => {
    setPulls((p) => ({
      ...p,
      [model.id]: { percent: 0, phase: 'starting', completed: 0, total: model.sizeBytes },
    }))
    try {
      await api.pullModel(model.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not start downloading ${model.name}`)
      setPulls((p) => {
        const next = { ...p }
        delete next[model.id]
        return next
      })
    }
  }

  const cancel = async (model: LocalModel) => {
    try {
      await api.cancelPull(model.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not cancel ${model.name}`)
    } finally {
      // Clear it either way: if the cancel did not land, the next poll puts
      // the row back rather than leaving a button that appears to do nothing.
      setPulls((p) => {
        const next = { ...p }
        delete next[model.id]
        return next
      })
    }
  }

  const remove = async (model: LocalModel) => {
    try {
      await api.removeModel(model.id)
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not remove ${model.name}`)
    }
  }

  const installedCount = models.filter((m) => m.installed).length
  const usedBytes = models.reduce((sum, m) => sum + (m.installedSize ?? 0), 0)

  return (
    <div className="mx-auto h-full w-full max-w-4xl overflow-y-auto px-6 py-8">
      <div className="mb-1 flex items-baseline justify-between gap-4">
        <h1 className="text-xl font-extrabold tracking-display">Models</h1>
        {installedCount > 0 && (
          <span className="text-xs text-content-muted">
            {installedCount} installed · {formatBytes(usedBytes)} on disk
          </span>
        )}
      </div>
      {/*
        Two sentences on purpose. The first is true in every case and shows
        immediately; the second says where the models will actually run, and
        waits until the answer is known.

        What used to be here -- "run entirely on your own machine. Nothing
        leaves the device" -- is true only when a device is paired. On the
        website with none, models are downloaded to and run on the server, so
        the page was promising privacy it could not keep. The page already
        knows which case it is in, so it can simply say.
      */}
      <p className="mb-5 text-sm text-content-muted">
        Download open models and run them with the engine NevTan installs for you —
        there is nothing separate to set up.{' '}
        {status &&
          (status.agentConnected ? (
            <>They run on your paired device, and their answers never leave it.</>
          ) : (
            <>
              They run on NevTan's own server. To run them on your own machine instead,
              pair a device from{' '}
              <Link to="/settings" className="underline hover:text-content">
                Settings
              </Link>
              .
            </>
          ))}
      </p>

      {status && !status.runtimeInstalled && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />
          {/*
            The server's own message is the whole message. It knows whether
            this deployment runs models at all, whether a device is paired, and
            what the person reading can actually do about it -- none of which
            this page can tell. The instruction that used to sit here told
            every user to run a script in a repository they do not have.
          */}
          <p>{status.message}</p>
        </div>
      )}

      {error && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/5 p-3 text-sm text-danger">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <DeviceCapabilityCard />

      <div className="mb-4 flex items-center gap-2 rounded-lg bg-surface-sunken px-3 py-2">
        <Search size={14} className="text-content-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search models — try “coder”, “small”, or “embedding”"
          className="w-full bg-transparent text-sm outline-none placeholder:text-content-muted"
        />
      </div>

      {loading ? (
        <p className="text-sm text-content-muted">Loading models…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-content-muted">No models match that search.</p>
      ) : (
        <div className="space-y-8">
          {groups.map(({ category, models: inCategory }) => (
            <section key={category}>
              {/* A single group is the whole list, and a heading over the
                  whole list names nothing. Shown only once there is a
                  distinction to draw. */}
              {groups.length > 1 && (
                <div className="mb-3">
                  <h2 className="text-sm font-semibold">
                    {CATEGORY_LABELS[category].title}
                    <span className="ml-2 font-normal text-content-muted">{inCategory.length}</span>
                  </h2>
                  <p className="mt-0.5 text-xs text-content-muted">
                    {CATEGORY_LABELS[category].blurb}
                  </p>
                </div>
              )}
              <ul className="space-y-2">
                {inCategory.map((model) => (
                  <ModelRow
                    key={model.id}
                    model={model}
                    pull={pulls[model.id]}
                    canManage={Boolean(status?.runtimeInstalled)}
                    update={updates[model.id]}
                    onUpdate={() => void applyUpdate(model)}
                    onPull={() => void pull(model)}
                    onRemove={() => void remove(model)}
                    onCancel={() => void cancel(model)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function ModelRow({
  model,
  pull,
  canManage,
  update,
  onPull,
  onRemove,
  onCancel,
  onUpdate,
}: {
  model: LocalModel
  pull?: { percent: number; phase: string; completed: number; total: number }
  canManage: boolean
  update?: ModelUpdateStatus
  onPull: () => void
  onRemove: () => void
  onCancel: () => void
  onUpdate: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const downloading = pull != null && !model.installed
  // Only UPDATE_AVAILABLE counts. UNKNOWN means the publisher could not be
  // reached, which is not a reason to prompt anyone to re-download.
  const updatable = model.installed && update?.state === 'UPDATE_AVAILABLE'

  return (
    <li className="rounded-lg border border-border bg-surface-raised p-3">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <Cpu size={18} className="mt-0.5 shrink-0 text-content-muted" />

        <div className="min-w-[14rem] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{model.name}</span>
            <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-[11px]">{model.id}</code>
            {model.installed && (
              <span className="flex items-center gap-1 rounded bg-success/15 px-1.5 py-0.5 text-[10px] font-medium text-success">
                <Check size={10} /> Installed
              </span>
            )}
            {updatable && (
              <span
                className="flex items-center gap-1 rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning"
                title="The publisher has released different weights for this model"
              >
                <RefreshCw size={10} /> Update available
              </span>
            )}
            <ModelFitBadge tier={model.fitTier} reason={model.fitReason} />
          </div>
          <p className="mt-0.5 text-xs text-content-muted">{model.description}</p>
          {model.fitReason && model.fitTier !== 'UNKNOWN' && (
            <p className="mt-0.5 text-[11px] text-content-muted">{model.fitReason}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-content-muted">
            <span className="flex items-center gap-1">
              <HardDrive size={11} />
              {formatBytes(model.installedSize ?? model.sizeBytes)}
            </span>
            {model.estimatedBytes > 0 && (
              <span title="Memory used while answering, which is more than the download">
                ~{formatBytes(model.estimatedBytes)} in use
              </span>
            )}
            <span>{model.parameters}</span>
            {/* A diffusion model has no context window and states none, which
                rendered as "0 context" -- a specific claim, and a false one.
                Shown only where there is a number to show. */}
            {model.contextTokens > 0 && (
              <span>{formatTokens(model.contextTokens)} context</span>
            )}
            {/* "tools" is skipped: the chip below says the same thing with a
                tooltip, and rendering both printed it twice on every model
                that declares it. */}
            {model.capabilities
              .filter((c) => c !== 'tools')
              .map((c) => (
                <span key={c} className="rounded bg-surface-sunken px-1.5 py-0.5">
                  {c}
                </span>
              ))}
            {/* Tools stay off for models that cannot call them reliably, so
                say that here rather than letting the chat silently lack them.

                Only for models that answer in a conversation at all. An image
                model was being labelled "chat only", which reads as a
                limitation of a thing it was never going to do. */}
            {answersInChat(model) && (
              <span
                className="rounded bg-surface-sunken px-1.5 py-0.5"
                title={
                  model.supportsTools
                    ? 'This model can use NevTan tools.'
                    : 'Chat only. This model is too small to call tools reliably, so they stay disabled.'
                }
              >
                {model.supportsTools ? 'tools' : 'chat only'}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
          {model.installed ? (
            confirming ? (
              <>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-sunken"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirming(false)
                    onRemove()
                  }}
                  className="rounded-md bg-danger px-2 py-1 text-xs font-medium text-white"
                >
                  Delete {formatBytes(model.installedSize ?? model.sizeBytes)}
                </button>
              </>
            ) : (
              <>
                {updatable && (
                  <button
                    type="button"
                    onClick={onUpdate}
                    disabled={!canManage}
                    // The size is on the button because this re-downloads the
                    // whole file; nobody should discover that afterwards.
                    className="flex items-center gap-1 rounded-md border border-warning px-2 py-1 text-xs font-medium text-warning hover:bg-warning/10 disabled:opacity-40"
                  >
                    <RefreshCw size={12} /> Update {formatBytes(update.upstreamSizeBytes || model.sizeBytes)}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  disabled={!canManage}
                  className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-danger hover:bg-danger/10 disabled:opacity-40"
                >
                  <Trash2 size={12} /> Remove
                </button>
              </>
            )
          ) : downloading ? (
            <>
              <span className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-content-muted">
                <Loader2 size={12} className="animate-spin" />
                {pull.percent > 0 ? `${pull.percent}%` : pull.phase}
              </span>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-sunken"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onPull}
              disabled={!canManage}
              className="flex items-center gap-1 rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-accent-content disabled:opacity-40"
              title={canManage ? undefined : 'The local inference engine is not installed yet'}
            >
              <Download size={12} /> Get
            </button>
          )}
        </div>
      </div>

      {downloading && (
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between text-[11px] text-content-muted">
            <span className="tabular-nums">
              {formatBytes(pull.completed)} / {formatBytes(pull.total || model.sizeBytes)}
              {pull.percent > 0 && ` (${pull.percent}%)`}
            </span>
            <span>{pull.phase}</span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={pull.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Downloading ${model.name}`}
            className="h-2 overflow-hidden rounded-full bg-surface-sunken"
          >
            {pull.percent > 0 ? (
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-500 ease-out"
                style={{ width: `${pull.percent}%` }}
              />
            ) : (
              // Nothing is reported until the first bytes land. A bar frozen
              // at zero reads as a stall, so it moves to show it is alive.
              <div className="h-full w-1/3 animate-pulse rounded-full bg-accent/50" />
            )}
          </div>
        </div>
      )}
    </li>
  )
}

function formatBytes(bytes: number): string {
  if (!bytes) return '—'
  const gb = bytes / 1_000_000_000
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  return `${Math.round(bytes / 1_000_000)} MB`
}

function formatTokens(tokens: number): string {
  return tokens >= 1000 ? `${Math.round(tokens / 1000)}K` : String(tokens)
}
